/**
 * Minimale Google Analytics 4-integratie.
 *
 * De meet-ID wordt bij de Vite-build in de publieke bundle gebakken. Zonder
 * geldige ID gebeurt bewust helemaal niets: lokale ontwikkeling en builds voor
 * tests mogen geen verkeer naar de productieproperty sturen.
 *
 * Dit bestand stuurt alleen GA4's automatische basisgebeurtenissen. Er zijn
 * geen eigen events, user-ID's of routewissels; deze site heeft één URL en
 * verandert die tijdens het gebruik niet.
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID?.trim() ?? '';

function isGa4MeasurementId(value: string): boolean {
  return /^G-[A-Z0-9]+$/i.test(value);
}

if (isGa4MeasurementId(measurementId)) {
  // gtag.js leest de wachtrij die hieronder wordt aangelegd; het script mag
  // dus vóór of na de commando's binnenkomen.
  const tag = document.createElement('script');
  tag.async = true;
  tag.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.append(tag);

  window.dataLayer = window.dataLayer ?? [];

  // LET OP: `arguments` doorgeven, niet een array van dezelfde waarden.
  // gtag.js herkent een commando aan de vorm van wat er in `dataLayer` staat en
  // leest alleen een echt `arguments`-object als zodanig; een array valt in de
  // afhandeling van GTM's eigen dataLayer-API en bereikt de GA4-property nooit.
  // Het script laadt dan wel en er komt geen enkele fout in de console — er
  // wordt alleen niets gemeten. Daarom een function-expressie: een pijlfunctie
  // heeft geen eigen `arguments`.
  const gtag: (...args: unknown[]) => void = function () {
    window.dataLayer!.push(arguments);
  };
  window.gtag = gtag;

  gtag('js', new Date());
  gtag('config', measurementId, {
    // Basisstatistieken wel, advertentieprofielen en cross-device-signalen niet.
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
}
